import { FC, useEffect, useState } from 'react';
import { ListPayload } from 'core/http/response';
import { useNavigate, useParams } from 'react-router-dom';
import { Declaration } from 'store/declarations/declaration';
import { ActionEvents, GroupActionEvents } from './DeclarationColumns';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { TablePaginationConfig, Table } from 'antd';
import { ColumnsType, ColumnGroupType, ColumnType } from 'antd/lib/table';
import { DeclarationInternalType } from '../../../store/declarations/enums/common/declaration-internal-type';
import { GvmsReferenceData } from '../../../store/declarations/uk/gvms-declaration';
import { GetComponentProps } from 'rc-table/lib/interface';
import { StyledTableHeaderCell, StyledTableWrapper } from 'components/new-ui/base/table/styles';
import './DeclarationsTable.css';
import {
    ExpandableConfig,
    FilterValue,
    SorterResult,
    TableCurrentDataSource,
    TableRowSelection,
} from 'antd/lib/table/interface';
import { hasPermissions } from 'config/roles/hasPermissions';
import useSession from 'hooks/useSession';
import { getExpandableConfig } from 'components/ui/base/table/expandedTableConfig';
import {
    hasIcs2Notifications,
    Ics2ListRow,
    Ics2TableRow,
    isIcs2GroupRow,
    toIcs2TableRows,
} from 'store/declarations/eu/ics2/ics2-declaration-list';
import getColumnsByDeclarationType from './utils/getColumnsByDeclarationType';
import getStandardColumns from './utils/getStandardColumns';
import getDeclarationPath from './utils/getDeclarationPath';
import getDeclarationTableRowClassName from './utils/getDeclarationTableRowClassName';

interface Props {
    data: ListPayload<Declaration> | null;
    handleDuplicate?: (declaration: Declaration) => void;
    onUnarchive?: (ids: string[]) => void;
    onArchive?: (ids: string[]) => void;
    loading?: boolean;
    onPrint?: (declarationId: string) => void;
    onDelete?: (declarationId: string) => void;
    onChange?:
        | ((
              pagination: TablePaginationConfig,
              filters: Record<string, FilterValue | null>,
              sorter: SorterResult<Declaration> | SorterResult<Declaration>[],
              extra: TableCurrentDataSource<Declaration>
          ) => void)
        | undefined;
    withPagination?: boolean;
    filterDataColumns?: (dataColumns: ColumnsType<any>) => (ColumnGroupType<any> | ColumnType<any>)[];
    gvmsReferenceData?: GvmsReferenceData;
    onBarcode?: (declaration: Declaration | undefined) => void;
    onRow?: GetComponentProps<any> | undefined;
    onCoreTemplateToggle?: (declaration: Declaration) => void;
    pagination?: TablePaginationConfig;
    basePath?: string;
    rowSelection?: TableRowSelection<Declaration> | undefined;
    groupActions?: GroupActionEvents;
}

const DeclarationTable: FC<Props> = ({
    data,
    handleDuplicate,
    onArchive,
    onUnarchive,
    loading,
    onPrint,
    onDelete,
    onChange,
    withPagination = true,
    filterDataColumns,
    gvmsReferenceData,
    onBarcode,
    onRow,
    onCoreTemplateToggle,
    pagination,
    basePath = '/',
    rowSelection,
    groupActions,
}) => {
    const navigate = useNavigate();
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
    const { userInfo } = useSession();
    const canDelete = hasPermissions(userInfo, 'declarations', 'DELETE');
    const canArchive = hasPermissions(userInfo, 'declarations', 'ARCHIVE');

    const { country, type } = useParams<{
        country: DeclarationCountry;
        type: DeclarationInternalType;
    }>();

    const isIcs2 = type === DeclarationInternalType.ENS;

    // The ICS2 listing endpoint returns grouped summary rows, not declarations — flatten them into
    // what the table renders.
    const ics2Rows = data?.list as unknown as Ics2ListRow[] | undefined;
    const tableDataSource = isIcs2 ? toIcs2TableRows(ics2Rows) : data?.list;

    // With nothing grouped on this page, neither the expand column nor the expand icon has a purpose.
    const ics2TableRows = isIcs2 ? (tableDataSource as Ics2TableRow[]) : [];
    const hasIcs2Groups = isIcs2 && ics2TableRows.some(isIcs2GroupRow);
    const showIcs2Notifications = isIcs2 && hasIcs2Notifications(ics2TableRows);

    useEffect(() => {
        setExpandedRowKeys([]);
    }, [data]);

    const actions: ActionEvents = {
        onDuplicate: handleDuplicate,
        onArchive,
        onUnarchive,
        onPrint,
        onDelete,
        onBarcode,
        onCoreTemplateToggle,
        filterDataColumns,
        groupActions,
    };

    // Inside /customs-declarations/{country}/{type} every row is of one type, so the table shows that
    // type's columns and actions. Anywhere else (e.g. the job overview, the drawers) the rows can be
    // a mix of declarations, so it shows the standard columns and actions.
    const columns =
        country && type
            ? getColumnsByDeclarationType({
                  country,
                  type,
                  actions,
                  user: userInfo,
                  declarations: data?.list,
                  gvms: { referenceData: gvmsReferenceData, canDelete, canArchive },
                  ics2: { hasGroups: hasIcs2Groups, hasNotifications: showIcs2Notifications },
              })
            : getStandardColumns(actions, userInfo, data?.list);

    const toggleExpanded = (key: React.Key) =>
        setExpandedRowKeys((keys) => (keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]));

    // Kept controlled so that clicking anywhere on a group row can open it.
    const ics2ExpandableConfig: ExpandableConfig<any> = {
        ...getExpandableConfig<any>((r) => !!r?.children?.length),
        expandedRowKeys,
        onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
    };

    return (
        <>
            <StyledTableWrapper>
                <Table
                    scroll={{
                        x: 'max-content',
                    }}
                    rowKey="id"
                    expandable={hasIcs2Groups ? ics2ExpandableConfig : undefined}
                    rowClassName={(record) => getDeclarationTableRowClassName(record, expandedRowKeys)}
                    columns={columns}
                    dataSource={tableDataSource as Declaration[] | undefined}
                    onRow={
                        onRow ||
                        ((record: Declaration) => ({
                            'data-testid': `declaration-table-row`,
                            onClick: (event) => {
                                const target = event.target as HTMLElement;
                                if (target.closest('.ant-table-selection-column')) {
                                    return;
                                }

                                event.stopPropagation();

                                // A group row is not a declaration of its own, so clicking it opens
                                // the group instead; its declarations navigate like any other row.
                                if (isIcs2GroupRow(record as unknown as Ics2TableRow)) {
                                    if (record.id) toggleExpanded(record.id);
                                    return;
                                }

                                navigate(getDeclarationPath(basePath, record));
                            },
                            style: { cursor: 'pointer' },
                        }))
                    }
                    pagination={
                        pagination ||
                        (withPagination
                            ? {
                                  current: (data?.pageNumber || 0) + 1,
                                  total: data?.total,
                                  showSizeChanger: false,
                                  pageSize: data?.pageSize,
                                  position: ['bottomCenter'],
                                  hideOnSinglePage: true,
                              }
                            : false)
                    }
                    rowSelection={rowSelection}
                    onChange={onChange}
                    loading={loading}
                    components={{
                        header: {
                            cell: (props: any) => <StyledTableHeaderCell {...props} />,
                        },
                    }}
                />
            </StyledTableWrapper>
        </>
    );
};

export default DeclarationTable;
